// The phone's database (IndexedDB). Everything the app keeps lives here; see Stores in shared/model.ts.
import { DB_NAME, DB_VERSION, type StoreName, type Stores } from "../shared/model.ts";
import { timeoutError } from "./util.ts";

// Opening with a higher DB_VERSION adds any store that is missing (version 6 added "trips").
const STORE_NAMES: StoreName[] = ["catches", "photos", "pins", "routes", "groups", "messages", "kv", "snapshots", "trips"];
let opening: Promise<IDBDatabase> | null = null;
let connection: IDBDatabase | null = null;

// Android may close the connection while the app is in the background: forget it and open a new one next time.
function forget(d: IDBDatabase): void {
  if (connection === d) { connection = null; opening = null; }
}

function idb(): Promise<IDBDatabase> {
  if (opening) return opening;
  const p = new Promise<IDBDatabase>((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, DB_VERSION);
    r.onupgradeneeded = () => {
      for (const name of STORE_NAMES) if (!r.result.objectStoreNames.contains(name)) r.result.createObjectStore(name, { keyPath: "id" });
    };
    r.onsuccess = () => {
      const d = r.result;
      d.onclose = () => forget(d);
      d.onversionchange = () => { d.close(); forget(d); };
      connection = d;
      resolve(d);
    };
    r.onerror = () => { opening = null; reject(r.error); };
    // another open copy of the app holding an old version: don't wait forever
    r.onblocked = () => setTimeout(() => { if (opening === p && !connection) { opening = null; reject(timeoutError()); } }, 8000);
  });
  opening = p;
  return p;
}

/** Runs one request in a transaction and resolves when the transaction is done (so writes are really saved).
 *  Gives up after 15 s instead of hanging, and reconnects once if the connection was closed under us. */
async function tx<T>(store: StoreName, mode: IDBTransactionMode, request: (s: IDBObjectStore) => IDBRequest<T> | void, retry = true): Promise<T | undefined> {
  const d = await idb();
  let tr: IDBTransaction;
  try { tr = d.transaction(store, mode); }
  catch (e) {
    if (retry && e instanceof DOMException && e.name === "InvalidStateError") { forget(d); return tx(store, mode, request, false); }
    throw e;
  }
  return new Promise<T | undefined>((resolve, reject) => {
    let out: T | undefined;
    const r = request(tr.objectStore(store));
    if (r) r.onsuccess = () => { out = r.result; };
    const timer = setTimeout(() => { try { tr.abort(); } catch { /* already finished */ } reject(timeoutError()); }, 15000);
    tr.oncomplete = () => { clearTimeout(timer); resolve(out); };
    tr.onerror = () => { clearTimeout(timer); reject(tr.error); };
    tr.onabort = () => { clearTimeout(timer); reject(tr.error ?? timeoutError()); };
  });
}

export async function dbAll<S extends StoreName>(store: S): Promise<Stores[S][]> {
  return (await tx(store, "readonly", (s) => s.getAll() as IDBRequest<Stores[S][]>)) ?? [];
}
export function dbGet<S extends StoreName>(store: S, id: string): Promise<Stores[S] | undefined> {
  return tx(store, "readonly", (s) => s.get(id) as IDBRequest<Stores[S] | undefined>);
}
export async function dbPut<S extends StoreName>(store: S, value: Stores[S]): Promise<void> {
  await tx(store, "readwrite", (s) => { s.put(value); });
}
export async function dbDel(store: StoreName, id: string): Promise<void> {
  await tx(store, "readwrite", (s) => { s.delete(id); });
}
